/* eslint-disable max-lines-per-function */
import React, { useEffect, useRef, useState } from "react"
import { Box, Button, Flex, FormControl, Heading, Image, Input, Select, SimpleGrid, Tag, Text, Textarea } from "@chakra-ui/react"
import { StaticPageHeader } from "../../components/commons/static-page-header"
import location from "../../assets/images/location.png"
import { breadCrumbFirstLevel, pageContentWidth } from "../../common/commonConstants"
import { BreadCrumb } from "../../components/commons/breadcrumb"
import { type contactusInterface } from "./contactus.store"
import { SUBMIT_FORM } from "./contactus.support"
import { useMutation } from "@apollo/client"
import { RHeading3PT } from "../../components/commons/RHeading3PT"
import { ContactUsBanner } from "../../components/images"
import { Colors } from "../../theme/colors"
import { SubPage, SubPageContent } from "../../components/SubPage"

const TURNSTILE_SITE_KEY = "0x4AAAAAADJdndOfvqgu2xOB"

const formControlStyles = {
  my: "13px"
}
const inputStyles = {
  bg: "rgba(0, 0, 0, 0.03)",
  borderRadius: "0"
}
export const Contact = (): JSX.Element => {
  const linkStyle = {
    lineHeight: "20px",
    color: Colors.Primary.c1
  }
  // Form States
  const [inputName, setInputName] = useState("")
  const [inputEmail, setInputEmail] = useState("")
  const [inputPhone, setInputPhone] = useState("")
  const [inputRelatesTo, setInputRelatesTo] = useState<string>("")
  const [inputContactPreference, setInputContactPreference] = useState<string>("")
  const [inputEnquiry, setInputEnquiry] = useState("")
  const [submitFormMutation, { data, loading: formSubmitloading, error: formSubmitError }] = useMutation(SUBMIT_FORM)
  const [turnstileToken, setTurnstileToken] = useState<string>("")
  const [turnstileError, setTurnstileError] = useState<string>("")
  const turnstileContainerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    const renderWidget = (): void => {
      if (!turnstileContainerRef.current || !(window as any).turnstile) return
      if (widgetIdRef.current) {
        try { (window as any).turnstile.remove(widgetIdRef.current) } catch (_) {}
      }
      widgetIdRef.current = (window as any).turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "light",
        callback: (token: string) => { setTurnstileToken(token); setTurnstileError("") },
        "expired-callback": () => { setTurnstileToken("") }
      })
    }

    const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')
    if (!existingScript) {
      const script = document.createElement("script")
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js"
      script.async = true
      script.defer = true
      script.onload = renderWidget
      document.head.appendChild(script)
    } else if ((window as any).turnstile) {
      renderWidget()
    } else {
      // Script tag present but not yet ready — poll briefly
      const poll = setInterval(() => {
        if ((window as any).turnstile) { clearInterval(poll); renderWidget() }
      }, 100)
      setTimeout(() => clearInterval(poll), 5000)
    }

    return () => {
      if (widgetIdRef.current && (window as any).turnstile) {
        try { (window as any).turnstile.remove(widgetIdRef.current) } catch (_) {}
      }
      widgetIdRef.current = null
    }
  }, [])

  // formSubmit
  const handleFormSubmit = (formData: contactusInterface, submitFormQuery: any): void => {
    if (!formData.turnstileToken) {
      setTurnstileError("Please complete the security check.")
      return
    }
    setTurnstileError("")
    submitFormQuery({
      variables: {
        input: { ...formData }
      }
    })
  }

  const clearInput = (): void => {
    setInputName("")
    setInputEmail("")
    setInputPhone("")
    setInputRelatesTo("")
    setInputContactPreference("")
    setInputEnquiry("")
    setTurnstileToken("")
    if (widgetIdRef.current && (window as any).turnstile) {
      try { (window as any).turnstile.reset(widgetIdRef.current) } catch (_) {}
    }
  }

  useEffect(() => {
    if (data) {
      clearInput()
    }
  }, [data])
  return (
    <SubPage props={{ id: "contactUsTop" }}>
      <Box margin="auto">
        <StaticPageHeader title="Contact Us" bg={ContactUsBanner} props={{ backgroundPosition: "0 25%" }} />
        <BreadCrumb
          options={{
            ...breadCrumbFirstLevel,
            thirdLevel: { title: "Contact Us" }
          }}
        />
        <SubPageContent innerBoxProps={{ w: ["100%", "100%", "100%", pageContentWidth, "70%", "70%"] }}>
          <Box margin="auto" flexWrap={"wrap"} justifyContent="space-between" mt="12px" className="aboutus">
            <Box margin="auto" textAlign={"center"} id="aboutus">
              <Flex justify={"center"} mb={["20px", "20px", "24px"]} mt={["18px", "18px", "28px"]}>
                <RHeading3PT text="About Us" />
              </Flex>
              <Heading margin="auto" variant="RHeading3PT" textAlign={"center"} w={["100%", "100%", "600px"]} color="blackText">
                Snap Fire Systems are an Australian-owned and based manufacturer of industry-leading fire collars.
              </Heading>
              <Box mt={["32px", "32px", "26px"]}>
                <Text mb={["24px", "24px", "8px"]} variant={"RButton2"} textAlign={["left", "left", "center"]}>
                  With a focus on innovation, we are committed to producing penetration seals that deliver the highest grade of fire protection at the
                  best value possible. We are constantly engaging with industry in order to tailor our products to suit practical challenges
                  encountered during passive fire design and installation. All of our products comply with the National Construction Codes (N.C.C.)
                  and are tested in accordance with the Australian Standards AS1530.4 and AS4072.1.
                </Text>
                <Text mb={["24px", "24px", "9px"]} variant={"RButton2"} textAlign={["left", "left", "center"]}>
                  We are unparalleled in the provision of access to our&nbsp;
                  <a href="/test-reports" style={{ ...linkStyle }}>
                    test data and assessments
                  </a>
                  &nbsp;so as to make the job of compliance for the end user always a snap. Snap Fire Collars are marketed and sold internationally.
                </Text>
                <Text mb={["18px", "18px", "8px"]} variant={"RButton2"} ml={[0, 0, "1px"]} textAlign={["left", "left", "center"]}>
                  See our UK website here:&nbsp;
                  <a target="_blank" href="https://suresnap.co.uk" style={{ ...linkStyle }} rel="noreferrer">
                    suresnap.co.uk
                  </a>
                </Text>
                <Text variant={"RButton2"} ml={["1px", "1px", 0]} textAlign={["left", "left", "center"]}>
                  See our distributors&nbsp;
                  <a href="/distributors" style={{ ...linkStyle }}>
                    here
                  </a>
                  , or please get in touch if you can't find what you're looking for.
                </Text>
              </Box>
            </Box>
            <Box mt={["34px", "34px", "54px"]}>
              <Flex align={"center"} justify={"center"} ml={["8px", "8px", "0"]}>
                <RHeading3PT text="Get in Touch" />
              </Flex>
              <SimpleGrid
                columns={[1, 1, 2]}
                spacing={["32px", "32px", "90px"]}
                alignItems="flex-start"
                mt={["20px", "20px", "24px"]}
                pb={["0px", "0px", "8px"]}
              >
                <Box fontSize={"16px"}>
                  <Box mb={["32px", "32px", "28px"]} id="contactUs">
                    <Text mb={["16px", "16px", "14px"]} variant={"RHeading3PT"}>
                      Phone Us
                    </Text>
                    <Flex flexDir={["column", "column", "row"]}>
                      Local Enquiries:&nbsp;&nbsp;
                      <Box mt={["8px", "8px", 0]}>
                        <a href="tel:1300764626" style={{ ...linkStyle }}>
                          1300 76 46 26
                        </a>
                      </Box>
                    </Flex>
                    <Flex flexDir={["column", "column", "row"]} mt={["8px", "8px", "14px"]}>
                      International Enquiries:&nbsp;&nbsp;
                      <Box mt={["8px", "8px", 0]}>
                        <a href="tel:61733905420" style={{ ...linkStyle }}>
                          +61 7 3390 5420
                        </a>
                      </Box>
                    </Flex>
                  </Box>
                  <Box mb={["32px", "32px", "22px"]}>
                    <Text mb={["16px", "16px", "10px"]} w={["210px", "210px", "210px"]} variant={"RHeading3PT"}>
                      Product Information
                    </Text>
                    <Text>
                      Please contact&nbsp;
                      <a href="/distributors" style={{ ...linkStyle }}>
                        your local distributor
                      </a>
                      &nbsp;for supply and advice about our products.
                    </Text>
                  </Box>
                  <Box mt={["28px", "28px", "20px"]}>
                    <Text mb="17px" variant={"RHeading3PT"}>
                      Location
                    </Text>
                    <Text>1343 Wynnum Road, Tingalpa Qld 4173.</Text>
                    <Image mt="17px" src={location} w={["328px", "328px", "395px"]} h={["188px", "188px", "226px"]} />
                  </Box>
                </Box>
                <Box fontSize={"16px"} pl={[0, 0, "34px"]}>
                  <Box mb={["31px", "31px", "25px"]}>
                    <Text mb="15px" variant={"RHeading3PT"}>
                      Email Us
                    </Text>
                    <Text>Please enter your contact details and enquiry below. We will respond as soon as possible.</Text>
                  </Box>
                  <form
                    method="post"
                    id="emailus"
                    onSubmit={e => {
                      e.preventDefault()
                      handleFormSubmit(
                        {
                          name: inputName,
                          emailAddress: inputEmail,
                          phone: inputPhone,
                          inquiry_type: inputRelatesTo,
                          contact_preference: inputContactPreference,
                          content: inputEnquiry,
                          turnstileToken
                        },
                        submitFormMutation
                      )
                    }}
                  >
                    <FormControl {...formControlStyles}>
                      <Input
                        onChange={e => {
                          setInputName(e.target.value)
                        }}
                        type="text"
                        value={inputName}
                        placeholder="Your Name"
                        required={true}
                        {...inputStyles}
                      />
                    </FormControl>
                    <FormControl {...formControlStyles}>
                      <Input
                        onChange={e => {
                          setInputEmail(e.target.value)
                        }}
                        type="email"
                        value={inputEmail}
                        placeholder="Your Email"
                        required={true}
                        {...inputStyles}
                      />
                    </FormControl>
                    <FormControl {...formControlStyles}>
                      <Input
                        onChange={e => {
                          setInputPhone(e.target.value)
                        }}
                        type="tel"
                        value={inputPhone}
                        placeholder="Your Phone"
                        required={true}
                        {...inputStyles}
                      />
                    </FormControl>
                    <FormControl {...formControlStyles}>
                      <Select
                        onChange={e => {
                          setInputRelatesTo(e.target.value)
                        }}
                        value={inputRelatesTo}
                        color={inputRelatesTo === "" ? "gray" : "black"}
                        placeholder="My enquiry relates to"
                        {...inputStyles}
                        required={true}
                      >
                        <option value="A particular product">A particular product</option>
                        <option value="Technical question">Technical question</option>
                        <option value="Other">Other</option>
                      </Select>
                    </FormControl>
                    <FormControl {...formControlStyles}>
                      <Select
                        onChange={e => {
                          setInputContactPreference(e.target.value)
                        }}
                        value={inputContactPreference}
                        color={inputContactPreference === "" ? "gray" : "black"}
                        placeholder="I would prefer to be contacted by"
                        {...inputStyles}
                        required={true}
                      >
                        <option value="Phone">Phone</option>
                        <option value="Email">Email</option>
                      </Select>
                    </FormControl>
                    <FormControl {...formControlStyles}>
                      <Textarea
                        onChange={e => {
                          setInputEnquiry(e.target.value)
                        }}
                        value={inputEnquiry}
                        placeholder="Your Enquiry"
                        {...inputStyles}
                        required={true}
                      />
                    </FormControl>

                    <Box my="13px">
                      <div ref={turnstileContainerRef} />
                    </Box>
                    {turnstileError && (
                      <Text color="red.500" fontSize="sm" mb="2">
                        {turnstileError}
                      </Text>
                    )}
                    {formSubmitError && (
                      <Text color="red.500" fontSize="sm" mb="2">
                        {formSubmitError.message}
                      </Text>
                    )}

                    <Button
                      mt="4"
                      variant="Button1RegularOutline"
                      minW="40px"
                      fontSize={"16px"}
                      fontFamily={"Work Sans"}
                      bg="primary"
                      minH={"20px"}
                      height={"20px"}
                      boxSizing="content-box"
                      lineHeight={["19px", "19px", "20px"]}
                      color="white"
                      py="6px"
                      px="18px"
                      type="submit"
                      _hover={{ bg: "primary" }}
                      isLoading={formSubmitloading}
                      isDisabled={!turnstileToken}
                    >
                      Send
                    </Button>
                    <Button
                      mt="4"
                      ml="4"
                      variant="RButton2"
                      py="6px"
                      px="18px"
                      _hover={{ bg: "transparent" }}
                      isLoading={formSubmitloading}
                      onClick={clearInput}
                    >
                      Cancel
                    </Button>
                  </form>
                  {data && (
                    <Tag mt="2" p="2" colorScheme={"green"}>
                      &nbsp; Thank you for your enquiry. We will be in touch shortly.
                    </Tag>
                  )}
                </Box>
              </SimpleGrid>
            </Box>
          </Box>
        </SubPageContent>
      </Box>
    </SubPage>
  )
}
